import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import armstrongLogo from "@/assets/armstrong-logo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("andy.a.holt@gmail.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiErrorMessage(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div
        className="hidden md:block relative bg-brand-navy"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,25,47,0.82), rgba(10,25,47,0.92)), url(https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={armstrongLogo} alt="Armstrong Private Security Ltd" className="h-12 w-auto object-contain" data-testid="login-brand-logo" />
          </div>
          <div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Where security<br />meets professionalism.
            </h1>
            <p className="mt-4 text-slate-300 max-w-md text-sm leading-relaxed">
              Build professional alarm & CCTV quotes, compare suppliers on quality, and present
              polished, branded quotations to your customers.
            </p>
            <div className="mt-6 h-1 w-24 bg-brand-gold" />
          </div>
          <div className="text-xs text-slate-400 font-mono">Fully licensed by the Cyprus Police</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <img src={armstrongLogo} alt="Armstrong Private Security Ltd" className="h-10 w-auto object-contain" data-testid="login-brand-logo-mobile" />
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Armstrong Business Hub</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-8">Access your quoting workspace.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full rounded-none bg-brand-navy hover:bg-brand-navy/90 text-white h-11 font-semibold"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
